import { createPart, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { defaultSection } from '@/features/storefront/sections/pages';
import { createSection, type Section } from '@/features/storefront/sections/schema';

/**
 * Tumble's product page, written down.
 *
 * A toy shop's product page: the toy photographed large on the left, and on
 * the right its name in the rounded heading face, the price with what it was
 * and what is saved, sizes and colours as swatches, stock, how many, add to
 * cart and buy it now, then the folded panels - description, key highlights,
 * shipping and returns. Under both, the related toys, then the reviews on the
 * lavender ground.
 *
 * As with the other themes' product pages, these are *defaults*: editable,
 * movable and removable in the customiser, carrying nothing that belongs to
 * the sample shop. The shipping panel draws nothing until the merchant writes
 * it.
 */

/**
 * One component of the page, with an id that does not change between renders
 * - see Hyper's `productPage.ts` for why a default must not mint a fresh id.
 */
function part(
  id: string,
  type: PartType,
  region: string,
  over: Partial<Omit<PagePart, 'id' | 'type'>> = {},
): PagePart {
  const base = createPart(type, region);
  return {
    ...base,
    ...over,
    id: `tumble-${id}`,
    region,
    options: { ...base.options, ...(over.options ?? {}) },
  };
}

/** The components of the product, in the regions `Product.tsx` lays out. */
export function tumbleProductParts(): PagePart[] {
  return [
    part('breadcrumbs', 'product_breadcrumbs', 'top'),

    part('gallery', 'product_gallery', 'gallery'),

    part('title', 'product_title', 'main'),
    part('price', 'product_price', 'main', {
      options: { show_compare_at: true, show_saving: true },
    }),
    part('summary', 'product_short_description', 'main'),
    part('variants', 'product_variants', 'main', { options: { style: 'swatches' } }),
    part('availability', 'product_availability', 'main'),
    part('quantity', 'product_quantity', 'main'),
    part('add', 'product_add_to_cart', 'main', { width: 'full' }),
    part('buy', 'product_buy_now', 'main', { width: 'full', options: { style: 'solid' } }),

    part('description', 'product_description', 'main', {
      label: 'Description',
      space_above: 'lg',
      options: { show_heading: true, collapsible: true },
    }),
    part('specs', 'product_specs', 'main', {
      label: 'Key highlights',
      options: { collapsible: true },
    }),
    part('delivery', 'product_shipping_info', 'main', {
      label: 'Shipping & returns',
      body: '',
      options: { collapsible: true, icon: 'truck' },
    }),
    part('share', 'product_share', 'main'),

    part('related', 'product_related', 'below'),
  ];
}

/** A section with a stable id, for the same reason {@link part} fixes one. */
function fixed(id: string, section: Section): Section {
  return { ...section, id: `tumble-${id}` };
}

/** The product page as a merchant on this theme first finds it. */
export function tumbleProductSections(): Section[] {
  return [
    defaultSection('product_details'),
    fixed('reviews', {
      ...createSection('product_reviews'),
      title: 'What parents say',
      background: 'surface',
      padding: 'lg',
    }),
  ];
}
